import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@chakra-ui/layout";
import { Button } from "@chakra-ui/button";
import {
  FileIcon,
  ListIcon,
  SidebarCardOrdersIcon,
  SidebarCardsIcon,
  SidebarEventsIcon,
  SidebarMembershipIcon,
  SidebarMembersIcon,
  SidebarPushNotificationIcon,
  TransferIcon,
  UserLockIcon,
  UsersIcon,
} from "../../styles/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  useIsSuperAdminOrSuperUser,
  useUserContext,
} from "../../context/UserContext";
import { usePermissions } from "../../hooks/usePermissions";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  IconButton,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { useBusinessContext } from "../../context/ModelContext";
import { LicencedProduct } from "../../enums/LicencedProduct";
import { LicencedProductFeatures } from "../../enums/LicencedProductFeatures";

// Helper function to check if current path matches menu item path
const isPathActive = (currentPath: string, menuPath: string): boolean => {
  // Remove trailing slashes for consistent comparison
  const cleanCurrentPath = currentPath.endsWith("/")
    ? currentPath.slice(0, -1)
    : currentPath;
  const cleanMenuPath = menuPath.endsWith("/")
    ? menuPath.slice(0, -1)
    : menuPath;

  // Handle exact match case
  if (cleanCurrentPath === cleanMenuPath) {
    return true;
  }

  // Handle nested route case - check if it's a true parent path
  if (cleanCurrentPath.startsWith(cleanMenuPath + "/")) {
    // For paths like '/businesses/DEV/members'
    // Make sure the menu item path doesn't partially match another path segment
    // For example, '/businesses/DEV/membership-requests' should not match '/businesses/DEV/members'
    const remainingPath = cleanCurrentPath.substring(cleanMenuPath.length + 1);
    const nextSegmentEnd = remainingPath.indexOf("/");
    const nextSegment =
      nextSegmentEnd === -1
        ? remainingPath
        : remainingPath.substring(0, nextSegmentEnd);

    // Get the last segment of menu path to compare
    const menuPathSegments = cleanMenuPath.split("/");
    const lastMenuSegment = menuPathSegments[menuPathSegments.length - 1];

    // Check that the next segment doesn't start with the last menu segment
    // This prevents '/businesses/DEV/membership-requests' from matching '/businesses/DEV/members'
    return !nextSegment.startsWith(lastMenuSegment);
  }

  return false;
};

interface MenuItemProps {
  children?: React.ReactNode;
  title: string;
  target: string;
  icon: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
}

interface ProductNameMenuItemGroupProps {
  children?: React.ReactNode;
  title: string;
}

const ProductNameMenuItemGroup = ({
  title,
  children,
}: ProductNameMenuItemGroupProps) => (
  <Accordion
    allowToggle={true}
    allowMultiple={true}
    defaultIndex={[0]}
    width="100%"
  >
    <AccordionItem
      bgColor="cherryBackground.100"
      borderTopWidth={0}
      borderBottomWidth={0}
    >
      {({ isExpanded }) => (
        <>
          <h2>
            <AccordionButton
              flexDirection={"row-reverse"}
              sx={{
                "&:focus": {
                  boxShadow: "none",
                },
              }}
            >
              <Box flex="1" textAlign="left" fontSize="xs" color={"white"}>
                {title}
              </Box>
              <ChevronRightIcon
                color="white"
                fontSize="xl"
                style={{
                  transform: isExpanded ? "rotate(90deg)" : undefined,
                  transition: "transform 0.2s",
                  transformOrigin: "center",
                }}
              />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} p="0">
            {children}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  </Accordion>
);

const DesktopMenuItem = ({ title, target, icon, children }: MenuItemProps) => {
  const location = useLocation();
  const [active, setActive] = useState(false);

  // Force re-evaluation of the active state whenever location changes
  useEffect(() => {
    setActive(isPathActive(location.pathname, target));
  }, [location.pathname, target]);

  return (
    <AccordionItem
      bgColor="cherryBackground.100"
      borderTopWidth={0}
      borderBottomWidth={0}
    >
      <h2>
        <AccordionButton>
          <Button
            as={Link}
            to={target}
            borderRadius="none"
            flex="1"
            textAlign="left"
            background={active ? "cherryBackgroundHover.100" : "none"}
            color="white"
            justifyContent="left"
            fontSize="sm"
            _hover={{ bgColor: "cherryBackgroundHover.100" }}
            _focus={{ bgColor: "cherryBackgroundHover.100" }}
          >
            <Box as="span" display="block" color="white" fontSize="sm" mr={2}>
              {icon}
            </Box>
            {title}
          </Button>
          {children && <AccordionIcon color="white" />}
        </AccordionButton>
      </h2>
      {children}
    </AccordionItem>
  );
};

const MobileMenuItem = ({ title, target, icon, children }: MenuItemProps) => {
  const location = useLocation();
  const [active, setActive] = useState(false);

  // Force re-evaluation of the active state whenever location changes
  useEffect(() => {
    setActive(isPathActive(location.pathname, target));
  }, [location.pathname, target]);

  return (
    <AccordionItem bgColor="cherryUi.700">
      <h2>
        <AccordionButton>
          <Button
            as={Link}
            to={target}
            borderRadius="none"
            flex="1"
            textAlign="left"
            background={active ? "cherryUi.600" : "none"}
            color="white"
            justifyContent="left"
            _hover={{ bgColor: "cherryUi.600" }}
          >
            <Box as="span" display="block" color="white" fontSize="sm" mr={2}>
              {icon}
            </Box>
            {title}
          </Button>
          {children && <AccordionIcon color="white" />}
        </AccordionButton>
      </h2>
      {children}
    </AccordionItem>
  );
};

const BusinessMenu = ({
  businessId,
  MenuItem,
}: {
  isDesktop: boolean;
  businessId: string;
  MenuItem: React.ComponentType<MenuItemProps>;
}) => {
  // Get location to use as part of the key to force re-renders
  const location = useLocation();
  const isSuperAdminOrSuperUser = useIsSuperAdminOrSuperUser();
  const businessSummary = useBusinessContext();

  const [
    hasReadDetails,
    hasUserManagement,
    canSearchMembers,
    hasFinancialReportAccess,
    hasMembershipReportAccess,
    canInviteToCard,
  ] = usePermissions(
    "Member.ReadDetails",
    "User.Management",
    "Member.Search",
    "Reporting.Financial",
    "Reporting.Membership",
    "Member.InviteToCard"
  );

  const businessSummaryTitle = canSearchMembers ? "Members" : "Venues";

  const isPushNotificationsEnabled = useMemo(() => {
    return (
      (businessSummary?.EnabledFeatures ?? {})[
        LicencedProduct.CherryPass
      ]?.includes(LicencedProductFeatures.CherryPass_PushNotifications) ?? false
    );
  }, [businessSummary]);

  return (
    // This minWidth is necessary to ensure an empty container when the user has no permissions
    // To keep the logo and the page content aligned
    <Box minWidth={"200px"}>
      {hasReadDetails && (
        <MenuItem
          key={`members-${location.pathname}`}
          target={`/businesses/${businessId}/members`}
          title={businessSummaryTitle}
          icon={<SidebarMembersIcon boxSize="20px" />}
        />
      )}
      {hasReadDetails && (
        <MenuItem
          key={`events-${location.pathname}`}
          target={`/businesses/${businessId}/events`}
          title={"Events"}
          icon={<SidebarEventsIcon boxSize="20px" />}
        />
      )}

      {canInviteToCard && (
        <ProductNameMenuItemGroup title={"Cherrypay"}>
          <MenuItem
            key={`cards-${location.pathname}`}
            target={`/businesses/${businessId}/cards`}
            title="Cards"
            icon={<SidebarCardsIcon boxSize="20px" />}
          />
          <MenuItem
            key={`card-orders-${location.pathname}`}
            target={`/businesses/${businessId}/card-orders`}
            title="Card Orders"
            icon={<SidebarCardOrdersIcon boxSize="20px" />}
          />
        </ProductNameMenuItemGroup>
      )}

      {hasMembershipReportAccess && (
        <ProductNameMenuItemGroup title={"Cherrypass"}>
          <MenuItem
            key={`membership-${location.pathname}`}
            target={`/businesses/${businessId}/membership-requests`}
            title="Membership"
            icon={<SidebarMembershipIcon boxSize="20px" />}
          />
          <MenuItem
            key={`renewals-${location.pathname}`}
            target={`/businesses/${businessId}/membership-renewal-requests`}
            title="Renewals"
            icon={<TransferIcon boxSize="20px" />}
          />
          {isSuperAdminOrSuperUser && isPushNotificationsEnabled && (
            <MenuItem
              key={`push-notifications-${location.pathname}`}
              target={`/businesses/${businessId}/push-notifications`}
              title="Push Notifications"
              icon={<SidebarPushNotificationIcon boxSize="20px" />}
            />
          )}
        </ProductNameMenuItemGroup>
      )}

      {(hasMembershipReportAccess ||
        hasFinancialReportAccess ||
        hasUserManagement) && (
        <ProductNameMenuItemGroup title={"Portal Management"}>
          {hasUserManagement && (
            <MenuItem
              key={`users-${location.pathname}`}
              target={`/businesses/${businessId}/users`}
              title="Users"
              icon={<UsersIcon boxSize="20px" />}
            />
          )}
          {hasUserManagement && (
            <MenuItem
              key={`roles-rbac-${location.pathname}`}
              target={`/businesses/${businessId}/roles-rbac`}
              title="Roles"
              icon={<UserLockIcon boxSize="20px" />}
            />
          )}
          {(hasMembershipReportAccess || hasFinancialReportAccess) && (
            <MenuItem
              key={`reports-${location.pathname}`}
              target={`/businesses/${businessId}/reports`}
              title="Reports"
              icon={<FileIcon boxSize="20px" />}
            />
          )}
        </ProductNameMenuItemGroup>
      )}
    </Box>
  );
};

const SidebarContainer = ({
  children,
  isCollapsed,
  toggleCollapse,
}: {
  children: React.ReactNode;
  isCollapsed: boolean;
  toggleCollapse: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box position="relative" height="100%">
      <Box
        position="absolute"
        top="15px"
        right="-5px"
        zIndex="10"
        width={"24px"}
        height={"24px"}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {(isHovered || isCollapsed) && (
          <IconButton
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            icon={isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            size="sm"
            background={"cherryUi.800"}
            colorScheme="cherryUi"
            borderRadius="full"
            sx={{
              "&:focus": {
                boxShadow: "none",
              },
            }}
            onClick={toggleCollapse}
          />
        )}
      </Box>
      <motion.div
        animate={{
          width: isCollapsed ? "40px" : "100%",
          opacity: isCollapsed ? 0 : 1,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ height: "100%", overflowY: "auto", maxHeight: "100vh" }}
      >
        {children}
      </motion.div>
    </Box>
  );
};

export const AppMenu = ({ isDesktop }: { isDesktop: boolean }) => {
  const { businessId } = useParams();
  const { userBusinessId } = useUserContext();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();

  const MenuItem = isDesktop ? DesktopMenuItem : MobileMenuItem;

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <SidebarContainer isCollapsed={isCollapsed} toggleCollapse={toggleCollapse}>
      <Accordion
        allowToggle
        width="100%"
        sx={{
          "& .chakra-accordion__item:last-of-type": {
            borderBottomWidth: "0px",
          },
          visibility: isCollapsed ? "hidden" : "visible",
        }}
      >
        {!userBusinessId && (
          <MenuItem
            key={`businesses-${location.pathname}`}
            target="/businesses"
            icon={<ListIcon boxSize="20px" />}
            title="Businesses"
          />
        )}

        {businessId && (
          <BusinessMenu businessId={businessId} MenuItem={MenuItem} isDesktop />
        )}
      </Accordion>
    </SidebarContainer>
  );
};
